/**
 * **OVERSHIELD** (RAW Core p.105): "This system provides HP
 * that disappears at the end of the scene or when a specified condition is met. The user only
 * retains the highest value of OVERSHIELD applied — it does not stack. Damage is dealt to
 * OVERSHIELD first, then HP. OVERSHIELD can push a character past their maximum HP. It can't
 * benefit from healing but otherwise benefits normally from anything that would affect HP and
 * damage (i.e., reduction, armor, etc)."
 *
 * Overshield is a **system tag**, not a weapon-attack tag — nothing here reads a frozen
 * `WeaponProfile`. It is two small mechanisms over the existing generic resource bag
 * ({@link OVERSHIELD_RESOURCE}, absent = 0), with **ZERO new step ops**:
 *
 *   1. **The keep-highest grant** ({@link grantOvershieldStep}) — `resource_set overshield
 *      max(current, value)`, the max computed at EMIT time (resolvers hold state, so the
 *      recorded step is already grounded and replay-safe).
 *   2. **The damage-pipeline stop** ({@link absorbIntoOvershield})
 *      — sits AFTER `mitigate()` and BEFORE the `hp_delta`-emitting damage cascade in every
 *      damage arm of the attack spine ({@link import("./resolve/damageApply").applyDamageInstance}
 *      for a hit, {@link import("./resolve/damageApply").applyReliableFloorDamage} for a miss),
 *      GM damage, the burn tick and every damage packet (`resolve/damagePacket.ts`), draining the
 *      shield via effective `resource_delta`s. Sitting after
 *      mitigate makes RAW's "benefits normally from reduction/armor" true by construction.
 *
 * **Expiry: none.** "End of the scene" = encounter teardown — the resource
 * dies with the encounter state, nothing to build. "When a specified condition is met" belongs
 * to the *granting content's* own duration machinery (the duration bus, later) — the effect that
 * grants a conditional Overshield owns clearing it; this module deliberately has no timer.
 */

import type { Combatant, ResourceSetStep, ResourceDeltaStep } from "@lancer-vtt/domain";
import { OVERSHIELD_RESOURCE } from "@lancer-vtt/domain";

/** A combatant's current Overshield value (absent = 0 — the common resource convention). */
export function overshieldValue(combatant: Combatant): number {
  return combatant.current.resources?.[OVERSHIELD_RESOURCE] ?? 0;
}

export type GrantOvershieldArgs = {
  /** The combatant receiving the shield (its CURRENT value feeds the keep-highest max). */
  combatant: Combatant;
  /** The granted Overshield value (the effect's number, e.g. an NPC feature's `Overshield 5`). */
  value: number;
};

/**
 * The **keep-highest Overshield grant** (RAW p.105 "The user only retains the
 * highest value of OVERSHIELD applied — it does not stack"): emit `resource_set overshield
 * max(current, value)`, the max computed HERE at emit time so the recorded step is grounded
 * and replays rules-free. Returns `null` when the grant changes nothing (a lower-or-equal
 * grant onto an existing shield — the no-stack case — or a non-positive value), so callers
 * skip a no-op step. Absence reads as 0, so a plain `prev` round-trips the inverse; no
 * `prevAbsent`/`resource_clear` (that machinery exists only for resources whose absence means
 * something OTHER than 0, like Loading's `?? 1`).
 *
 * **This helper is the single body a future authored atom wraps** — an atom with zero authored
 * consumers is exactly the INERT bucket the builtins ledger flags, so the atom is documented
 * here, NOT built. Intended atom shape: **one effects-engine op** (e.g.
 * `{ op: "overshield", target, value }`) whose interpreter case is nothing but
 * `grantOvershieldStep({ combatant: resolvedTarget, value })` — no second keep-highest
 * implementation, no bespoke state. Until the atom exists, the GM/CLI grant path (`lvtt encounter
 * overshield`, which drives THIS helper) is the table-usable writer.
 */
export function grantOvershieldStep(args: GrantOvershieldArgs): ResourceSetStep | null {
  const { combatant, value } = args;
  const current = overshieldValue(combatant);
  const next = Math.max(current, value);
  if (next <= 0 || next === current) return null; // no-stack (keep-highest kept the old value) / nothing granted.
  return { op: "resource_set", target: combatant.id, key: OVERSHIELD_RESOURCE, val: next, prev: current };
}

export type AbsorbOvershieldArgs = {
  /** The combatant taking the damage, as every step before this one left it: its shield is read off it. */
  target: Combatant;
  /** The POST-mitigation damage about to become `hp_delta` (the pipeline-stop input). */
  amount: number;
};

/**
 * The **Overshield damage-pipeline stop**: drain `min(shield, amount)` from the
 * target's shield, emitting an **effective** `resource_delta` (never more than the
 * shield actually holds — like `hpDeltaStep`'s post-clamp delta, the effective amount is what
 * keeps the self-inverting `resource_delta` exact on undo), and return the `remaining` damage
 * that falls through to HP. Call sites sit AFTER `mitigate()` (so reduction/armor already
 * applied — RAW "benefits normally from anything that would affect HP and damage") and BEFORE
 * the `hp_delta`-emitting damage cascade (RAW "Damage is dealt to OVERSHIELD first, then HP").
 *
 * **Scope:** weapon damage (hit, Reliable-on-miss, a protector's split share)
 * and **burn** route through this (burn IS damage — RAW p.67's burn deals damage, and p.105
 * stops damage at the shield); **heat does NOT** (heat is its own track, not damage — the
 * heat path never calls this). The burn *mark* (`burn_set`) is also untouched: marking is
 * not damage, so a shielded burn hit still marks in full and re-burns on later ticks (each
 * tick's damage then absorbing again). **One call site:** `routeFinalDamage` (`resolve/damage.ts`),
 * which every damage site reaches through the damage packet (`resolve/damagePacket.ts`): the whole
 * packet for the non-attack sites and GM damage (`event-engine-damage` Phase 1, BC-001 to BC-006,
 * BC-008); its per-instance core for the attack spine's hit and protector shares and Reliable floor,
 * the burn tick and the `extra_damage` rider (BC-016/BC-007).
 * `overshieldBypass.characterization.test.ts` pins each non-attack site.
 *
 * **No running ledger.** Each site hands this the target as the steps before it left it (the
 * packet's earlier instances, an area fan-out's earlier targets: `event-engine-damage` D5, BC-023),
 * so the shield it reads is already what is left, and one resolution never spends it twice.
 */
export function absorbIntoOvershield(args: AbsorbOvershieldArgs): { steps: ResourceDeltaStep[]; absorbed: number; remaining: number } {
  const { target, amount } = args;
  const absorbed = Math.min(overshieldValue(target), Math.max(0, amount));
  if (absorbed <= 0) return { steps: [], absorbed: 0, remaining: Math.max(0, amount) };
  return {
    steps: [{ op: "resource_delta", target: target.id, key: OVERSHIELD_RESOURCE, delta: -absorbed }],
    absorbed,
    remaining: amount - absorbed,
  };
}
